import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CompanyResponse } from '../../../../@shared/models/response.module';
import { StorageService } from '../../../../@core/services/storage.service';

export interface OnboardingItem {
  id: string;
  title: string;
  description: string;
  icon: string;
  tab: string;
  completed: boolean;
  dismissed: boolean;
}

@Component({
  selector: 'app-company-onboarding-guide',
  standalone: false,
  templateUrl: './company-onboarding-guide.html',
  styleUrl: './company-onboarding-guide.css'
})
export class CompanyOnboardingGuide implements OnInit, OnChanges {
  @Input() company: CompanyResponse;
  @Input() hasNewsPosts: boolean = false;
  @Input() hasContacts: boolean = false;
  @Input() hasSponsors: boolean = false;
  @Input() hasDocuments: boolean = false;
  @Input() hasEvents: boolean = false;
  @Output() navigateToTab = new EventEmitter<string>();

  items: OnboardingItem[] = [];
  isCollapsed: boolean = false;
  isFullyDismissed: boolean = false;

  private storageKey: string = '';

  constructor(private storageService: StorageService) {}

  ngOnInit() {
    this.initializeItems();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['company'] || changes['hasNewsPosts'] || changes['hasContacts'] ||
        changes['hasSponsors'] || changes['hasDocuments'] || changes['hasEvents']) {
      this.initializeItems();
    }
  }

  private initializeItems() {
    if (!this.company) return;

    this.storageKey = `onboarding_dismissed_${this.company.company_id}`;
    const dismissedData = this.getDismissedState();

    // Check if fully dismissed
    this.isFullyDismissed = dismissedData.fullyDismissed || false;
    this.isCollapsed = dismissedData.collapsed || false;

    // Define all onboarding items
    this.items = [
      {
        id: 'images',
        title: 'companyOnboarding.addImages',
        description: 'companyOnboarding.addImagesDesc',
        icon: 'fa-image',
        tab: 'information',
        completed: this.hasImages(),
        dismissed: dismissedData.items?.images || false
      },
      {
        id: 'news',
        title: 'companyOnboarding.addNews',
        description: 'companyOnboarding.addNewsDesc',
        icon: 'fa-newspaper',
        tab: 'feed',
        completed: this.hasNewsPosts,
        dismissed: dismissedData.items?.news || false
      },
      {
        id: 'contacts',
        title: 'companyOnboarding.addContacts',
        description: 'companyOnboarding.addContactsDesc',
        icon: 'fa-address-book',
        tab: 'information',
        completed: this.hasContactInfo(),
        dismissed: dismissedData.items?.contacts || false
      },
      {
        id: 'sponsors',
        title: 'companyOnboarding.addSponsors',
        description: 'companyOnboarding.addSponsorsDesc',
        icon: 'fa-handshake',
        tab: 'sponsors',
        completed: this.hasSponsors,
        dismissed: dismissedData.items?.sponsors || false
      },
      {
        id: 'documents',
        title: 'companyOnboarding.addDocuments',
        description: 'companyOnboarding.addDocumentsDesc',
        icon: 'fa-file-alt',
        tab: 'documents',
        completed: this.hasDocuments,
        dismissed: dismissedData.items?.documents || false
      },
      {
        id: 'payment',
        title: 'companyOnboarding.setupPayment',
        description: 'companyOnboarding.setupPaymentDesc',
        icon: 'fa-credit-card',
        tab: 'billing',
        completed: this.isPaymentSetup(),
        dismissed: dismissedData.items?.payment || false
      },
      {
        id: 'events',
        title: 'companyOnboarding.createEvent',
        description: 'companyOnboarding.createEventDesc',
        icon: 'fa-calendar-plus',
        tab: 'events',
        completed: this.hasEvents,
        dismissed: dismissedData.items?.events || false
      }
    ];
  }

  private isPaymentSetup(): boolean {
    // Check if Stripe is connected OR custom payment is enabled
    return (this.company.stripe_account_status === 'active' &&
            this.company.stripe_charges_enabled === true) ||
           this.company.custom_payment_enabled === true;
  }

  private hasImages(): boolean {
    return !!this.company.company_logo_url && !!this.company.header_image_url;
  }

  private hasContactInfo(): boolean {
    // Check if company has basic contact information filled in
    return !!(this.company.email && this.company.phone);
  }

  get visibleItems(): OnboardingItem[] {
    return this.items.filter(item => !item.completed && !item.dismissed);
  }

  get completedCount(): number {
    return this.items.filter(item => item.completed).length;
  }

  get totalCount(): number {
    return this.items.length;
  }

  get progressPercent(): number {
    return Math.round((this.completedCount / this.totalCount) * 100);
  }

  get shouldShow(): boolean {
    return !this.isFullyDismissed && this.visibleItems.length > 0;
  }

  onItemClick(item: OnboardingItem) {
    this.isCollapsed = true;
    this.saveDismissedState();
    this.navigateToTab.emit(item.tab);
  }

  dismissItem(item: OnboardingItem, event: Event) {
    event.stopPropagation();
    item.dismissed = true;
    this.saveDismissedState();
  }

  dismissAll() {
    this.isFullyDismissed = true;
    this.saveDismissedState();
  }

  toggleCollapse() {
    this.isCollapsed = !this.isCollapsed;
    this.saveDismissedState();
  }

  resetOnboarding() {
    this.isFullyDismissed = false;
    this.isCollapsed = false;
    this.items.forEach(item => item.dismissed = false);
    this.saveDismissedState();
  }

  private getDismissedState(): any {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  }

  private saveDismissedState() {
    const state = {
      fullyDismissed: this.isFullyDismissed,
      collapsed: this.isCollapsed,
      items: this.items.reduce((acc, item) => {
        acc[item.id] = item.dismissed;
        return acc;
      }, {} as Record<string, boolean>)
    };
    localStorage.setItem(this.storageKey, JSON.stringify(state));
  }
}
